import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { getBotInfo } from '../lib/api';

/**
 * "Share to Telegram" button — opens a Telegram deep-link
 *   `https://t.me/<bot_username>?start=share_<kind>_<ref>`
 * Clicking it routes the user into a DM with the bot; the bot's
 * `start_command` decodes the payload and DMs back a snapshot.
 *
 * Kinds:
 *   - `addr`  → Address Overview for `<ref>` (an EVM address)
 *   - `tok`   → Token Stats (24h) for `<ref>` (token id or address)
 *   - `ctr`   → Latest Verified Contracts (ref ignored, send `share_ctr`)
 *
 * The bot username is fetched once via `/api/bot/info` and cached at module
 * scope to avoid an extra round-trip on every card mount.
 */
let cachedBotInfo = null;

export const ShareToTelegram = ({ kind, payload, label = 'Share to Telegram', className = '', size = 'sm' }) => {
  const [botUsername, setBotUsername] = useState(cachedBotInfo?.username || null);

  useEffect(() => {
    if (cachedBotInfo) return;
    let mounted = true;
    getBotInfo()
      .then((info) => {
        cachedBotInfo = info;
        if (mounted && info?.username) setBotUsername(info.username);
      })
      .catch(() => { /* silent — button just hides if we can't resolve */ });
    return () => { mounted = false; };
  }, []);

  if (!botUsername) return null;

  // Payload format: share_<kind>[_<ref>]   (Telegram limits start payload to 64 chars)
  let startParam;
  if (kind === 'ctr') {
    startParam = 'share_ctr';
  } else if (kind === 'scan' && payload) {
    startParam = `scan_${payload}`;
  } else if (payload) {
    startParam = `share_${kind}_${payload}`;
  } else {
    return null;
  }
  // Telegram only accepts [A-Za-z0-9_-] in start payloads — strip anything else.
  startParam = startParam.replace(/[^A-Za-z0-9_-]/g, '');
  if (startParam.length > 64) return null;

  const href = `https://t.me/${botUsername}?start=${startParam}`;
  const testId = `share-to-telegram-${kind}${payload ? `-${payload.slice(0, 10)}` : ''}`;

  return (
    <Button
      asChild
      variant="outline"
      size={size}
      className={`gap-2 ${className}`}
      data-testid={testId}
    >
      <a href={href} target="_blank" rel="noopener noreferrer">
        <Send className="h-4 w-4" />
        {label}
      </a>
    </Button>
  );
};

export default ShareToTelegram;
